import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { NamedResponse } from '../models/response-models/named-response.model';
import { Signout } from '../models/signout.model';
import { HttpParamsService } from './http-params.service';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private companyApiUrl: string = environment.companyApiUrl;
  private readonly GET_ALL_PRODUCT_CATEGORIES_LITE = (): string =>
    `${this.companyApiUrl}/ProductCategories/Lite`;
  private readonly GET_ALL_PRODUCT_DEPARTMENTS_LITE = (): string =>
    `${this.companyApiUrl}/ProductDepartments/Lite`;
  private readonly GET_ALL_BRANDS_LITE = (): string => `${this.companyApiUrl}/Brands/Lite`;
  private companyAppsCache = new Map<number, Observable<any>>();
  private appsCache = new Map<string, Observable<any>>();
  private permissionsCache = new Map<
    string,
    {
      permissions: string[];
      timestamp: number;
      appId: number;
    }
  >();

  private readonly CACHE_EXPIRY_MS = 60 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private httpParamsService: HttpParamsService,
  ) {}
  logout(userDetails: Signout) {
    const url = `${this.companyApiUrl}/Auth/SignOut`;
    return this.http.post(url, userDetails);
  }

  getAllCountries() {
    const url = `${this.companyApiUrl}/Countries`;
    return this.http.get(url);
  }

  getStates(countryId: number) {
    const url = `${this.companyApiUrl}/States?CountryId=${countryId}`;
    return this.http.get(url);
  }

  getTimezones() {
    const url = `${this.companyApiUrl}/TimeZones`;
    return this.http.get(url);
  }
  getStores(companyId: number) {
    let url = `${this.companyApiUrl}/Stores/Lite?CompanyId=${companyId}`;
    return this.http.get(url);
  }

  getProductCategories(queryParams: Record<string, any>) {
    const params: HttpParams = this.httpParamsService.buildCleanHttpParams(queryParams);
    let url = `${this.companyApiUrl}/ProductCategories/Lite`;
    return this.http.get(url, { params });
  }

  getProductDepartments(queryParams: Record<string, any>) {
    const params: HttpParams = this.httpParamsService.buildCleanHttpParams(queryParams);
    let url = `${this.companyApiUrl}/ProductDepartments/Lite`;
    return this.http.get(url, { params });
  }

  getBrands(queryParams: Record<string, any>) {
    const params: HttpParams = this.httpParamsService.buildCleanHttpParams(queryParams);
    let url = `${this.companyApiUrl}/Brands/Lite`;
    return this.http.get(url, { params });
  }

  getStoreById(id: number) {
    const url = `${this.companyApiUrl}/Stores/${id}`;
    return this.http.get(url);
  }

  getApps(): Observable<any> {
    const cacheKey = 'all';

    if (this.appsCache.has(cacheKey)) {
      return this.appsCache.get(cacheKey)!;
    }

    const url = `${this.companyApiUrl}/Apps`;
    const request$ = this.http.get(url).pipe(shareReplay(1));

    this.appsCache.set(cacheKey, request$);
    return request$;
  }
  getCompanyApps(companyId: number): Observable<any> {
    if (this.companyAppsCache.has(companyId)) {
      return this.companyAppsCache.get(companyId)!;
    }

    const url = `${this.companyApiUrl}/Companies/${companyId}/Apps`;
    const request$ = this.http.get(url).pipe(shareReplay(1));

    this.companyAppsCache.set(companyId, request$);
    return request$;
  }

  setCachedPermissions(userId: number, appId: number, permissions: string[]): void {
    const key = `${userId}_${appId}`;
    this.permissionsCache.set(key, {
      permissions: [...permissions],
      timestamp: Date.now(),
      appId,
    });
  }

  getCachedPermissions(userId: number, appId: number): string[] | null {
    const key = `${userId}_${appId}`;
    const cached = this.permissionsCache.get(key);

    if (!cached) {
      return null;
    }

    const isExpired = Date.now() - cached.timestamp > this.CACHE_EXPIRY_MS;
    if (isExpired) {
      this.permissionsCache.delete(key);
      return null;
    }
    return [...cached.permissions];
  }

  clearCachedPermissions(userId: number): void {
    const keysToDelete: string[] = [];

    this.permissionsCache.forEach((value, key) => {
      if (key.startsWith(`${userId}_`)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      this.permissionsCache.delete(key);
    });
  }

  clearAllCachedPermissions(): void {
    this.permissionsCache.clear();
  }

  getPermissionsCacheStatus(): { userId: number; appId: number; count: number; age: number }[] {
    const status: { userId: number; appId: number; count: number; age: number }[] = [];

    this.permissionsCache.forEach((value, key) => {
      const [userIdStr, appIdStr] = key.split('_');
      status.push({
        userId: parseInt(userIdStr, 10),
        appId: parseInt(appIdStr, 10),
        count: value.permissions.length,
        age: Date.now() - value.timestamp,
      });
    });

    return status;
  }

  getProductDepartmentsLite(queryParams: Record<string, any>): Observable<NamedResponse[]> {
    const params: HttpParams = this.httpParamsService.buildCleanHttpParams(queryParams);
    return this.http.get<NamedResponse[]>(this.GET_ALL_PRODUCT_DEPARTMENTS_LITE(), {
      params,
    });
  }

  getProductCategoriesLite(queryParams: Record<string, any>): Observable<NamedResponse[]> {
    const params: HttpParams = this.httpParamsService.buildCleanHttpParams(queryParams);
    return this.http.get<NamedResponse[]>(this.GET_ALL_PRODUCT_CATEGORIES_LITE(), {
      params,
    });
  }

  getBrandsLite(queryParams: Record<string, any>): Observable<NamedResponse[]> {
    const params: HttpParams = this.httpParamsService.buildCleanHttpParams(queryParams);
    return this.http.get<NamedResponse[]>(this.GET_ALL_BRANDS_LITE(), {
      params,
    });
  }
}
